import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";

const field = "min-h-[42px] w-full border border-[#C69A3B]/60 bg-white px-2 text-sm";
const EMPTY = { title_es: "", title_en: "", year: "", notes_es: "", notes_en: "" };

export default function AdminRestorations() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const refs = useRef({});

  const load = useCallback(() => {
    api.get("/admin/restorations").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const create = async () => {
    if (!draft.title_es.trim()) {
      toast.error(t("admin.restorationTitleRequired"));
      return;
    }
    try {
      await api.post("/admin/restorations", { ...draft, year: draft.year.trim() });
      setDraft(EMPTY);
      toast.success(t("admin.siteSaved"));
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const save = async (item, updates) => {
    try {
      await api.patch(`/admin/restorations/${item.id}`, updates);
      toast.success(t("admin.siteSaved"));
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`${item.title_es}\n\n${t("admin.restorationDeleteConfirm")}`)) return;
    try {
      await api.delete(`/admin/restorations/${item.id}`);
      toast.success(t("admin.siteSaved"));
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const upload = async (item, key, file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/site-images", form);
      await save(item, { [key]: data.url });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const slot = (item, key, label) => (
    <div className="w-40">
      <p className="label-mono mb-2">{label}</p>
      <button
        type="button"
        data-testid={`restoration-${key}-btn-${item.id}`}
        onClick={() => refs.current[`${item.id}-${key}`]?.click()}
        className="grid h-28 w-full place-items-center overflow-hidden border border-[#C69A3B]/50 bg-[#EFEAE0]"
      >
        {item[key] ? (
          <img src={item[key]} alt="" className="h-full w-full object-cover" />
        ) : (
          <Upload size={16} className="text-[#C69A3B]" />
        )}
      </button>
      <input
        ref={(el) => (refs.current[`${item.id}-${key}`] = el)}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`restoration-${key}-input-${item.id}`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(item, key, file);
          e.target.value = "";
        }}
      />
    </div>
  );

  return (
    <div className="space-y-8" data-testid="admin-restoration-panel">
      <p className="text-sm text-[#5A5752]">{t("admin.restorationHelp")}</p>

      <div className="border border-[#C69A3B]/40 bg-white p-6">
        <p className="label-mono">{t("admin.restorationNew")}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ["title_es", "Título — Español"],
            ["title_en", "Title — English"],
            ["year", "Año (opcional, texto libre)"],
            ["notes_es", "Descripción — Español"],
            ["notes_en", "Description — English"],
          ].map(([key, label]) => (
            <label key={key} className="block">
              <span className="label-mono">{label}</span>
              <input
                className={field}
                data-testid={`restoration-new-${key}`}
                value={draft[key]}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
              />
            </label>
          ))}
        </div>
        <div className="mt-4">
          <GoldButton onClick={create} data-testid="restoration-new-btn">
            <Plus size={14} /> {t("admin.restorationAdd")}
          </GoldButton>
        </div>
      </div>

      <div className="space-y-5" data-testid="admin-restoration-list">
        {items.length === 0 && (
          <p className="text-sm text-[#5A5752]" data-testid="admin-restoration-empty">
            {t("restoration.empty")}
          </p>
        )}
        {items.map((item) => (
          <div
            key={item.id}
            data-testid={`admin-restoration-${item.id}`}
            className="flex flex-wrap items-start gap-5 border border-[#C69A3B]/40 bg-white p-5"
          >
            {slot(item, "before_image", t("restoration.before"))}
            {slot(item, "after_image", t("restoration.after"))}
            <div className="grid min-w-[260px] flex-1 gap-3 sm:grid-cols-2">
              {[
                ["title_es", "Título — Español"],
                ["title_en", "Title — English"],
                ["year", "Año (texto libre)"],
                ["notes_es", "Descripción — Español"],
                ["notes_en", "Description — English"],
              ].map(([key, label]) => (
                <label key={key} className="block">
                  <span className="label-mono">{label}</span>
                  <input
                    className={field}
                    data-testid={`restoration-${key}-${item.id}`}
                    defaultValue={item[key] ?? ""}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      if (String(item[key] ?? "") !== raw) save(item, { [key]: raw.trim() });
                    }}
                  />
                </label>
              ))}
              <label className="block">
                <span className="label-mono">{t("admin.restorationOrientation")}</span>
                <select
                  className={field}
                  data-testid={`restoration-orientation-${item.id}`}
                  value={item.orientation || "landscape"}
                  onChange={(e) => save(item, { orientation: e.target.value })}
                >
                  <option value="landscape">{t("restoration.landscape")}</option>
                  <option value="portrait">{t("restoration.portrait")}</option>
                  <option value="square">{t("restoration.square")}</option>
                </select>
              </label>
              <label className="flex items-end gap-2 text-xs">
                <input
                  type="checkbox"
                  data-testid={`restoration-visible-${item.id}`}
                  checked={item.visible !== false}
                  onChange={(e) => save(item, { visible: e.target.checked })}
                />
                {t("admin.restorationVisible")}
              </label>
            </div>
            <button
              type="button"
              data-testid={`restoration-delete-${item.id}`}
              onClick={() => remove(item)}
              className="inline-flex min-h-[42px] items-center gap-2 border border-[#8F3329] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
            >
              <Trash2 size={13} /> {t("btn.delete")}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
